import { useState, useEffect, useCallback, useRef } from 'react';
import { Modal } from 'react-bootstrap-v5';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { IUsuario } from '../../interfaces/IUsuario';
import { ILancamento } from '../../interfaces/ILancamento';
import ArquivoCardEsteiraService, { IAlteracaoComStatus } from '../../services/ArquivoCardEsteiraService';
import api from '../../services/Api';

interface Props {
    show: boolean;
    toggleModal: () => void;
    esteiraID: number;
    usuario: IUsuario | undefined;
}

const statusBadgeClass: Record<string, string> = {
    'Pendente': 'badge-light-warning',
    'Aprovada - Aguardando': 'badge-light-info',
    'Processando': 'badge-light-primary',
    'Concluída': 'badge-light-success',
    'Reprovada': 'badge-light-danger',
    'Erro': 'badge-light-danger',
};

const tipoBadgeClass: Record<string, string> = {
    'HISTORICO': 'badge-light-info',
    'VALOR': 'badge-light-primary',
    'CONTA': 'badge-light-warning',
    'ESTORNO': 'badge-light-danger',
};

const tipoLabel: Record<string, string> = {
    'C': 'Crédito',
    'D': 'Débito',
};

function formatarMoeda(v?: number | null) {
    if (v == null) return '-';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
}

function formatarData(d?: string | null) {
    if (!d) return '-';
    return new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function formatarDataCurta(d?: string | null) {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('pt-BR');
}

function LancamentoDetalhe({ lancamentoId }: { lancamentoId: number }) {
    const { t } = useTranslation();
    const [dados, setDados] = useState<ILancamento | null>(null);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState(false);

    useEffect(() => {
        setCarregando(true);
        setErro(false);
        api.get<ILancamento>(`Lancamento/${lancamentoId}`)
            .then(r => setDados(r.data))
            .catch(() => setErro(true))
            .finally(() => setCarregando(false));
    }, [lancamentoId]);

    if (carregando) {
        return (
            <div className='d-flex align-items-center gap-2 py-2 px-3 text-muted'>
                <span className='spinner-border spinner-border-sm'></span>
                {t('Carregando detalhes...')}
            </div>
        );
    }

    if (erro || !dados) {
        return (
            <div className='py-2 px-3 text-danger'>
                <i className='fas fa-exclamation-circle me-1'></i>
                {t('Não foi possível carregar os detalhes do lançamento.')}
            </div>
        );
    }

    return (
        <div className='d-flex flex-wrap gap-4 py-3 px-4' style={{ background: '#f9f9f9', borderTop: '1px solid #e9ecef' }}>
            <div>
                <div className='text-muted fs-8 fw-semibold text-uppercase mb-1'>{t('Valor')}</div>
                <div className='fw-bold text-gray-800'>{formatarMoeda(dados.valorProvisaoRecolher)}</div>
            </div>
            <div>
                <div className='text-muted fs-8 fw-semibold text-uppercase mb-1'>{t('Tipo')}</div>
                <div>
                    <span className={`badge ${dados.tipoLancamento === 'C' ? 'badge-light-success' : 'badge-light-danger'}`}>
                        {tipoLabel[dados.tipoLancamento] ?? dados.tipoLancamento}
                    </span>
                </div>
            </div>
            <div>
                <div className='text-muted fs-8 fw-semibold text-uppercase mb-1'>{t('Data')}</div>
                <div className='fw-semibold text-gray-700'>{formatarDataCurta(dados.dataLancamento)}</div>
            </div>
            <div>
                <div className='text-muted fs-8 fw-semibold text-uppercase mb-1'>{t('Status')}</div>
                <div className='fw-semibold text-gray-700'>{dados.status}</div>
            </div>
            <div style={{ flex: '1 1 200px' }}>
                <div className='text-muted fs-8 fw-semibold text-uppercase mb-1'>{t('Histórico')}</div>
                <div className='text-gray-700' style={{ wordBreak: 'break-word' }}>{dados.historico || '-'}</div>
            </div>
        </div>
    );
}

function ModalSolicitacoesAlteracao({ show, toggleModal, esteiraID, usuario }: Props) {
    const { t } = useTranslation();
    const [alteracoes, setAlteracoes] = useState<IAlteracaoComStatus[]>([]);
    const [carregando, setCarregando] = useState(false);
    const [processando, setProcessando] = useState<Set<number>>(new Set());
    const [expandido, setExpandido] = useState<number | null>(null);
    const [selecionados, setSelecionados] = useState<Set<number>>(new Set());
    const [aprovandoLote, setAprovandoLote] = useState(false);

    const podeGerenciar = usuario?.perfil === 'Administrador' || usuario?.responsavelContabil === true;

    const carregar = useCallback(async () => {
        if (!esteiraID) return;
        setCarregando(true);
        try {
            const data = await ArquivoCardEsteiraService.buscarTodasAlteracoes(esteiraID);
            setAlteracoes(data);
            setSelecionados(new Set());
        } catch {
            // silently fail on load
        } finally {
            setCarregando(false);
        }
    }, [esteiraID]);

    useEffect(() => {
        if (show) {
            setExpandido(null);
            carregar();
        }
    }, [show, carregar]);

    const toggleExpandido = (lancamentoId: number) => {
        setExpandido(prev => prev === lancamentoId ? null : lancamentoId);
    };

    const aprovar = async (id: number) => {
        const confirm = await Swal.fire({
            title: t('Aprovar solicitação'),
            text: t('Deseja aprovar esta solicitação de alteração?'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('Aprovar'),
            cancelButtonText: t('Cancelar'),
        });
        if (!confirm.isConfirmed) return;

        setProcessando(prev => new Set(prev).add(id));
        try {
            await ArquivoCardEsteiraService.aprovarAlteracao(id);
            await Swal.fire({ icon: 'success', title: t('Aprovado'), text: t('Solicitação aprovada e enviada para processamento.'), timer: 3000, showConfirmButton: false });
            await carregar();
        } catch (e: any) {
            const msg = e?.response?.data?.message || e?.message || t('Erro ao aprovar solicitação.');
            Swal.fire({ icon: 'error', title: t('Erro'), text: msg, timer: 5000, showConfirmButton: false });
        } finally {
            setProcessando(prev => { const s = new Set(prev); s.delete(id); return s; });
        }
    };

    const toggleSelecionado = (id: number) => {
        setSelecionados(prev => {
            const s = new Set(prev);
            if (s.has(id)) s.delete(id); else s.add(id);
            return s;
        });
    };

    const idsPendentes = alteracoes.filter(a => a.status === 'Pendente').map(a => a.id);
    const todosPendentesSelecionados = idsPendentes.length > 0 && idsPendentes.every(id => selecionados.has(id));
    const algumPendenteSelecionado = idsPendentes.some(id => selecionados.has(id));

    const toggleSelecionarTodos = () => {
        setSelecionados(todosPendentesSelecionados ? new Set() : new Set(idsPendentes));
    };

    const aprovarSelecionados = async () => {
        const confirm = await Swal.fire({
            title: t('Aprovar em lote'),
            text: t('Deseja aprovar as {{count}} solicitações selecionadas em lote?', { count: selecionados.size }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('Aprovar'),
            cancelButtonText: t('Cancelar'),
        });
        if (!confirm.isConfirmed) return;

        setAprovandoLote(true);
        try {
            const { total } = await ArquivoCardEsteiraService.aprovarAlteracoesEmLote(esteiraID, Array.from(selecionados));
            await Swal.fire({ icon: 'success', title: t('Enviado'), text: t('{{count}} alterações enviadas para aprovação em lote.', { count: total }), timer: 3000, showConfirmButton: false });
            await carregar();
        } catch (e: any) {
            const msg = e?.response?.data?.message || e?.message || t('Erro ao aprovar em lote.');
            Swal.fire({ icon: 'error', title: t('Erro'), text: msg, timer: 5000, showConfirmButton: false });
        } finally {
            setAprovandoLote(false);
        }
    };

    const reprovar = async (id: number) => {
        const confirm = await Swal.fire({
            title: t('Reprovar solicitação'),
            text: t('Deseja reprovar esta solicitação de alteração?'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('Reprovar'),
            cancelButtonText: t('Cancelar'),
            confirmButtonColor: '#d9534f',
        });
        if (!confirm.isConfirmed) return;

        setProcessando(prev => new Set(prev).add(id));
        try {
            await ArquivoCardEsteiraService.reprovarAlteracao(id);
            await Swal.fire({ icon: 'success', title: t('Reprovado'), text: t('Solicitação reprovada com sucesso.'), timer: 3000, showConfirmButton: false });
            await carregar();
        } catch (e: any) {
            const msg = e?.response?.data?.message || e?.message || t('Erro ao reprovar solicitação.');
            Swal.fire({ icon: 'error', title: t('Erro'), text: msg, timer: 5000, showConfirmButton: false });
        } finally {
            setProcessando(prev => { const s = new Set(prev); s.delete(id); return s; });
        }
    };

    const pendentes = alteracoes.filter(a => a.status === 'Pendente').length;
    const concluidas = alteracoes.filter(a => a.status === 'Concluída').length;
    const reprovadas = alteracoes.filter(a => a.status === 'Reprovada').length;
    const erros = alteracoes.filter(a => a.status === 'Erro').length;

    const colSpan = podeGerenciar ? 11 : 9;

    const checkboxTodosRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (checkboxTodosRef.current) {
            checkboxTodosRef.current.indeterminate = algumPendenteSelecionado && !todosPendentesSelecionados;
        }
    }, [algumPendenteSelecionado, todosPendentesSelecionados]);

    return (
        <Modal show={show} onHide={toggleModal} size='xl' centered>
            <Modal.Header closeButton>
                <Modal.Title>{t('SOLICITAÇÕES DE ALTERAÇÃO')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {carregando ? (
                    <div className='d-flex justify-content-center align-items-center py-10'>
                        <span className='spinner-border text-primary' role='status'></span>
                    </div>
                ) : (
                    <>
                        <div className='row g-3 mb-4'>
                            <div className='col-6 col-md-3'>
                                <div className='border rounded p-3 text-center'>
                                    <div className='fs-2 fw-bold text-warning'>{pendentes}</div>
                                    <div className='text-muted fs-7'>{t('Pendentes')}</div>
                                </div>
                            </div>
                            <div className='col-6 col-md-3'>
                                <div className='border rounded p-3 text-center'>
                                    <div className='fs-2 fw-bold text-success'>{concluidas}</div>
                                    <div className='text-muted fs-7'>{t('Concluídas')}</div>
                                </div>
                            </div>
                            <div className='col-6 col-md-3'>
                                <div className='border rounded p-3 text-center'>
                                    <div className='fs-2 fw-bold text-danger'>{reprovadas}</div>
                                    <div className='text-muted fs-7'>{t('Reprovadas')}</div>
                                </div>
                            </div>
                            <div className='col-6 col-md-3'>
                                <div className='border rounded p-3 text-center'>
                                    <div className='fs-2 fw-bold text-danger'>{erros}</div>
                                    <div className='text-muted fs-7'>{t('Erros')}</div>
                                </div>
                            </div>
                        </div>

                        {alteracoes.length === 0 ? (
                            <div className='d-flex flex-column align-items-center py-10 text-muted'>
                                <i className='fas fa-inbox fs-2x mb-3'></i>
                                <span>{t('Nenhuma solicitação de alteração encontrada para este período.')}</span>
                            </div>
                        ) : (
                            <div className='table-responsive'>
                                <table className='table table-hover table-sm align-middle'>
                                    <thead className='table-light'>
                                        <tr>
                                            {podeGerenciar && (
                                                <th>
                                                    <input
                                                        type='checkbox'
                                                        className='form-check-input'
                                                        ref={checkboxTodosRef}
                                                        checked={todosPendentesSelecionados}
                                                        onChange={toggleSelecionarTodos}
                                                        disabled={idsPendentes.length === 0}
                                                        title={t('Selecionar todas as pendentes')}
                                                    />
                                                </th>
                                            )}
                                            <th>#</th>
                                            <th>{t('Lançamento')}</th>
                                            <th>{t('Tipo')}</th>
                                            <th>{t('Valor / Detalhe')}</th>
                                            <th>{t('Solicitante')}</th>
                                            <th>{t('Solicitado em')}</th>
                                            <th>{t('Status')}</th>
                                            <th>{t('Processado por')}</th>
                                            <th>{t('Processado em')}</th>
                                            {podeGerenciar && <th>{t('Ações')}</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {alteracoes.map(item => (
                                            <>
                                                <tr key={item.id}>
                                                    {podeGerenciar && (
                                                        <td>
                                                            {item.status === 'Pendente' && (
                                                                <input
                                                                    type='checkbox'
                                                                    className='form-check-input'
                                                                    checked={selecionados.has(item.id)}
                                                                    onChange={() => toggleSelecionado(item.id)}
                                                                />
                                                            )}
                                                        </td>
                                                    )}
                                                    <td className='text-nowrap text-muted fs-7'>{item.id}</td>
                                                    <td className='text-nowrap'>
                                                        <button
                                                            className='btn btn-link btn-sm p-0 fw-semibold d-flex align-items-center gap-1'
                                                            onClick={() => toggleExpandido(item.lancamentoId)}
                                                            title={t('Ver detalhes do lançamento')}
                                                        >
                                                            #{item.lancamentoId}
                                                            <i className={`fas fa-chevron-${expandido === item.lancamentoId ? 'up' : 'down'} fs-8 text-muted`}></i>
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${tipoBadgeClass[item.tipo] ?? 'badge-light-secondary'}`}>
                                                            {item.tipo}
                                                        </span>
                                                    </td>
                                                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                        title={item.valor ?? '-'}>
                                                        {item.valor ?? '-'}
                                                    </td>
                                                    <td>{item.nomeCadastrou ?? item.usuarioCadastrouOperacaoId}</td>
                                                    <td className='text-nowrap'>{formatarData(item.dataCadastro)}</td>
                                                    <td>
                                                        <span className={`badge ${statusBadgeClass[item.status] ?? 'badge-light-secondary'}`}>
                                                            {item.status}
                                                            {item.processando && (
                                                                <span className='spinner-border spinner-border-sm ms-1' role='status'></span>
                                                            )}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {item.nomeAprou && <span className='text-success'>{item.nomeAprou}</span>}
                                                        {item.nomeReprou && <span className='text-danger'>{item.nomeReprou}</span>}
                                                        {!item.nomeAprou && !item.nomeReprou && '-'}
                                                    </td>
                                                    <td className='text-nowrap'>
                                                        {item.dataAprovacao ? formatarData(item.dataAprovacao) : ''}
                                                        {item.dataReprovacao ? formatarData(item.dataReprovacao) : ''}
                                                        {!item.dataAprovacao && !item.dataReprovacao && '-'}
                                                    </td>
                                                    {podeGerenciar && (
                                                        <td>
                                                            {item.status === 'Pendente' && (
                                                                <div className='d-flex gap-1'>
                                                                    <button
                                                                        className='btn btn-sm btn-light-success'
                                                                        onClick={() => aprovar(item.id)}
                                                                        disabled={processando.has(item.id)}
                                                                        title={t('Aprovar')}
                                                                    >
                                                                        {processando.has(item.id)
                                                                            ? <span className='spinner-border spinner-border-sm'></span>
                                                                            : <i className='fas fa-check'></i>}
                                                                    </button>
                                                                    <button
                                                                        className='btn btn-sm btn-light-danger'
                                                                        onClick={() => reprovar(item.id)}
                                                                        disabled={processando.has(item.id)}
                                                                        title={t('Reprovar')}
                                                                    >
                                                                        {processando.has(item.id)
                                                                            ? <span className='spinner-border spinner-border-sm'></span>
                                                                            : <i className='fas fa-times'></i>}
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </td>
                                                    )}
                                                </tr>
                                                {expandido === item.lancamentoId && (
                                                    <tr key={`detail-${item.id}`} style={{ background: 'transparent' }}>
                                                        <td colSpan={colSpan} style={{ padding: 0, border: 0 }}>
                                                            <LancamentoDetalhe lancamentoId={item.lancamentoId} />
                                                        </td>
                                                    </tr>
                                                )}
                                            </>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button className='btn btn-sm btn-light' onClick={carregar} disabled={carregando}>
                    <i className='fas fa-sync-alt me-1'></i>{t('Atualizar')}
                </button>
                {podeGerenciar && (
                    <button
                        className='btn btn-sm btn-light-success'
                        onClick={aprovarSelecionados}
                        disabled={selecionados.size === 0 || aprovandoLote}
                        title={t('Aprovar em uma única ação todas as solicitações marcadas')}
                    >
                        {aprovandoLote
                            ? <span className='spinner-border spinner-border-sm me-1'></span>
                            : <i className='fas fa-check-double me-1'></i>}
                        {t('Aprovar selecionados')} ({selecionados.size})
                    </button>
                )}
                <button className='btn btn-sm btn-secondary' onClick={toggleModal}>
                    {t('Fechar')}
                </button>
            </Modal.Footer>
        </Modal>
    );
}

export default ModalSolicitacoesAlteracao;
